<template>
    <div>
        <h3 class="ix-page-title-secondary mt-4">Zones de livraison</h3>

        <!-- Ce que le formulaire poste : la fenêtre d'édition ne fait que modifier ces zones. -->
        <template v-for="(zone, index) in zones">
            <input type="hidden" :name="'shipping_zones[' + index + '][key]'" :value="zone.key" :key="zone.key + '-key'">
            <input type="hidden" :name="'shipping_zones[' + index + '][id]'" :value="zone.id" v-if="zone.id" :key="zone.key + '-id'">
            <input type="hidden" :name="'shipping_zones[' + index + '][name]'" :value="zone.name" :key="zone.key + '-name'">
            <input type="hidden" :name="'shipping_zones[' + index + '][type]'" :value="zone.type" :key="zone.key + '-type'">
            <input type="hidden" :name="'shipping_zones[' + index + '][departments]'" :value="zone.departments.join(',')" :key="zone.key + '-dep'">
            <input type="hidden" :name="'shipping_zones[' + index + '][countries]'" :value="zone.countries.join(',')" :key="zone.key + '-cty'">
        </template>

        <div class="ix-table-box mb-2">
            <table class="ix-table">
                <tbody>
                    <tr v-for="(zone, index) in zones" :key="zone.key">
                        <td style="width: 240px;">
                            <strong>{{ zone.name }}</strong>
                            <span class="badge badge-secondary ml-1" v-if="zone.type === 'default'">Par défaut</span>
                        </td>
                        <td>
                            <template v-if="zone.type !== 'default'">
                                <span class="text-muted">{{ typeLabels[zone.type] }} ·</span>
                                <span v-if="summary(zone)">{{ summary(zone) }}</span>
                                <span v-else class="text-danger">aucun choisi</span>
                            </template>
                            <span v-else class="text-muted">Toutes les adresses qu'aucune autre zone ne couvre</span>
                        </td>
                        <td class="text-right text-nowrap" style="width: 100px;">
                            <span class="btn btn-sm transparent" @click="edit(index)" title="Modifier"><i class="far fa-pen"></i></span>
                            <span class="btn btn-sm transparent" @click="removeZone(index)" title="Supprimer"><i class="far fa-trash"></i></span>
                        </td>
                    </tr>
                    <tr v-if="zones.length === 0">
                        <td class="text-muted">Aucune zone.</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <button type="button" class="ix-btn link" @click="edit(null)"><i class="far fa-plus"></i> Ajouter une zone</button>

        <h3 class="ix-page-title-secondary mt-4">Tarifs par tranche de poids</h3>
        <div class="ix-table-box mb-2">
            <table class="ix-table ix-shipping-rates">
                <thead>
                    <tr>
                        <th style="width: 230px;">Tranche de poids</th>
                        <th class="text-center ix-shipping-rate-col" v-for="zone in zones" :key="zone.key">{{ zone.name }}</th>
                        <th>Mode d'expédition</th>
                        <th style="width: 60px;"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(bracket, index) in allBrackets" :key="bracket.uniq">
                        <td class="align-middle">
                            <input type="hidden" :name="'shipping_brackets[' + index + '][id]'" :value="bracket.id" v-if="bracket.id">
                            <!-- La dernière tranche n'a pas de borne : elle couvre tout ce qui dépasse. -->
                            <template v-if="bracket.isLast">
                                <input type="hidden" :name="'shipping_brackets[' + index + '][max_weight]'" value="">
                                Plus de {{ formatWeight(lowerBound(index)) }} kg
                            </template>
                            <div v-else class="d-flex align-items-center">
                                <span class="text-nowrap mr-2">de {{ formatWeight(lowerBound(index)) }} à</span>
                                <div class="input-group input-group-sm" style="width: 120px;">
                                    <input type="number" class="form-control text-center" min="0" step="0.001" required
                                           :name="'shipping_brackets[' + index + '][max_weight]'" v-model="bracket.max_weight"
                                           @change="sortBrackets">
                                    <div class="input-group-append">
                                        <span class="input-group-text">kg</span>
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td v-for="zone in zones" :key="zone.key" class="ix-shipping-rate-col">
                            <div class="input-group input-group-sm">
                                <input type="number" class="form-control text-center" min="0" step="0.01"
                                       :name="'shipping_brackets[' + index + '][rates][' + zone.key + ']'"
                                       v-model="bracket.rates[zone.key]">
                                <div class="input-group-append">
                                    <span class="input-group-text">€</span>
                                </div>
                            </div>
                        </td>
                        <td>
                            <input type="text" class="form-control form-control-sm"
                                   :name="'shipping_brackets[' + index + '][mode]'" v-model="bracket.mode">
                        </td>
                        <td class="text-center align-middle">
                            <span v-if="!bracket.isLast" class="btn btn-sm transparent" @click="brackets.splice(index, 1)" title="Supprimer"><i class="far fa-trash"></i></span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <button type="button" class="ix-btn link" @click="addBracket"><i class="far fa-plus"></i> Ajouter une tranche</button>
        <small class="form-text text-muted">
            Montants HT. Une case vide signifie un tarif sur devis.
        </small>

        <b-modal ref="zoneModal" size="lg" scrollable :title="editingIndex === null ? 'Nouvelle zone' : 'Modifier la zone'"
                 ok-title="Valider" cancel-title="Annuler" :ok-disabled="!draftValid" @ok="applyDraft">
            <div class="form-group">
                <label for="shipping-zone-name" class="required">Nom</label>
                <input type="text" class="form-control" id="shipping-zone-name" v-model="draft.name" placeholder="ex. Bretagne">
            </div>

            <div class="form-group">
                <label>Couvre</label>
                <div>
                    <div class="btn-group btn-group-toggle">
                        <label v-for="(label, type) in typeLabels" :key="type" class="btn btn-sm"
                               :class="[draft.type === type ? 'btn-primary' : 'btn-outline-secondary', { disabled: type === 'default' && defaultTakenBy }]">
                            <input type="radio" v-model="draft.type" :value="type" :disabled="type === 'default' && defaultTakenBy"> {{ label }}
                        </label>
                    </div>
                </div>
                <small class="form-text text-muted" v-if="defaultTakenBy">« {{ defaultTakenBy }} » est déjà la zone par défaut.</small>
            </div>

            <template v-if="draft.type === 'departments'">
                <div class="form-group">
                    <label>Ajouter une région</label>
                    <div>
                        <button type="button" v-for="(codes, region) in regionMap" :key="region"
                                class="btn btn-sm mr-1 mb-1" :class="regionState(codes) === 'all' ? 'btn-primary' : (regionState(codes) === 'some' ? 'btn-outline-primary' : 'btn-outline-secondary')"
                                @click="toggleRegion(codes)">{{ region }}</button>
                    </div>
                </div>
                <div class="form-group">
                    <input type="text" class="form-control form-control-sm mb-2" v-model="search" placeholder="Rechercher un département (nom ou numéro)">
                    <div class="ix-shipping-zone-choices">
                        <div class="custom-control custom-checkbox" v-for="department in filteredDepartments" :key="department.code">
                            <input type="checkbox" class="custom-control-input" :id="'dep-' + department.code"
                                   :value="department.code" v-model="draft.departments" :disabled="!!takenBy('departments', department.code)">
                            <label class="custom-control-label" :for="'dep-' + department.code">
                                {{ department.code }} {{ department.name }}
                                <small class="text-muted" v-if="takenBy('departments', department.code)">({{ takenBy('departments', department.code) }})</small>
                            </label>
                        </div>
                    </div>
                    <small class="form-text text-muted">{{ draft.departments.length }} sélectionné(s).</small>
                </div>
            </template>

            <template v-if="draft.type === 'countries'">
                <div class="form-group">
                    <button type="button" class="btn btn-sm" :class="europeState === 'all' ? 'btn-primary' : 'btn-outline-secondary'" @click="toggleEurope">
                        Union européenne (hors France)
                    </button>
                </div>
                <div class="form-group">
                    <input type="text" class="form-control form-control-sm mb-2" v-model="search" placeholder="Rechercher un pays">
                    <div class="ix-shipping-zone-choices">
                        <div class="custom-control custom-checkbox" v-for="country in filteredCountries" :key="country.code">
                            <input type="checkbox" class="custom-control-input" :id="'cty-' + country.code"
                                   :value="country.code" v-model="draft.countries" :disabled="!!takenBy('countries', country.code)">
                            <label class="custom-control-label" :for="'cty-' + country.code">
                                <span class="iti-flag mr-1" :class="country.code.toLowerCase()"></span>{{ country.name }}
                                <small class="text-muted" v-if="takenBy('countries', country.code)">({{ takenBy('countries', country.code) }})</small>
                            </label>
                        </div>
                    </div>
                    <small class="form-text text-muted">{{ draft.countries.length }} sélectionné(s).</small>
                </div>
            </template>

            <p class="text-muted mb-0" v-if="draft.type === 'default'">
                Couvre toutes les adresses qu'aucune autre zone ne couvre, à l'étranger comme en France.
            </p>
        </b-modal>
    </div>
</template>

<script>
    import Swal from 'sweetalert2';
    // Feuille de drapeaux du champ téléphone, réutilisée pour la liste des pays.
    import 'vue-phone-number-input/dist/vue-phone-number-input.css';

    export default {
        name: 'shipping-grid-form',
        props: ['oldZones', 'oldBrackets', 'countries', 'departments', 'regions', 'europeanUnion'],
        data() {
            return {
                zones: [],
                brackets: [],
                lastBracket: null,
                countryList: [],
                departmentList: [],
                regionMap: {},
                euCodes: [],
                editingIndex: null,
                draft: { name: '', type: 'departments', departments: [], countries: [] },
                search: '',
                typeLabels: { departments: 'Départements', countries: 'Pays', default: 'Zone par défaut' },
            };
        },
        computed: {
            draftValid() {
                if (!this.draft.name.trim()) return false;
                if (this.draft.type === 'departments') return this.draft.departments.length > 0;
                if (this.draft.type === 'countries') return this.draft.countries.length > 0;
                return true;
            },
            /** Nom de l'autre zone qui couvre déjà « tout le reste », s'il y en a une. */
            defaultTakenBy() {
                const other = this.zones.find((zone, index) => zone.type === 'default' && index !== this.editingIndex);
                return other ? other.name : null;
            },
            filteredDepartments() {
                const term = this.normalize(this.search);
                return this.departmentList.filter(d => !term || d.code.toLowerCase().startsWith(term) || this.normalize(d.name).includes(term));
            },
            filteredCountries() {
                const term = this.normalize(this.search);
                return this.countryList.filter(c => !term || c.code.toLowerCase() === term || this.normalize(c.name).includes(term));
            },
            /** Les tranches bornées, puis la tranche « plus de », toujours présente et toujours dernière. */
            allBrackets() {
                return this.lastBracket ? this.brackets.concat([this.lastBracket]) : this.brackets;
            },
            europeState() {
                return this.selectionState(this.euCodes.filter(code => !this.takenBy('countries', code)), this.draft.countries);
            },
        },
        methods: {
            normalize(value) {
                return String(value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
            },
            newKey() {
                return 'new-' + Date.now() + '-' + Math.floor(Math.random() * 1000);
            },
            /** Zone qui porte déjà ce département ou ce pays, hors zone en cours d'édition. */
            takenBy(field, code) {
                const other = this.zones.find((zone, index) => index !== this.editingIndex && zone.type === field && zone[field].indexOf(code) !== -1);
                return other ? other.name : null;
            },
            selectionState(codes, selected) {
                const count = codes.filter(code => selected.indexOf(code) !== -1).length;
                if (codes.length > 0 && count === codes.length) return 'all';
                return count > 0 ? 'some' : 'none';
            },
            regionState(codes) {
                return this.selectionState(codes.filter(code => !this.takenBy('departments', code)), this.draft.departments);
            },
            toggleCodes(field, codes) {
                const free = codes.filter(code => !this.takenBy(field, code));
                if (this.selectionState(free, this.draft[field]) === 'all') {
                    this.draft[field] = this.draft[field].filter(code => free.indexOf(code) === -1);
                } else {
                    this.draft[field] = this.draft[field].concat(free.filter(code => this.draft[field].indexOf(code) === -1));
                }
            },
            toggleRegion(codes) {
                this.toggleCodes('departments', codes);
            },
            toggleEurope() {
                this.toggleCodes('countries', this.euCodes);
            },
            /** Une ligne lisible : les régions entières par leur nom, le reste par numéro. */
            summary(zone) {
                if (zone.type === 'default') return 'Toutes les adresses non couvertes';

                if (zone.type === 'countries') {
                    if (this.euCodes.length && this.euCodes.every(code => zone.countries.indexOf(code) !== -1) && zone.countries.length === this.euCodes.length) {
                        return 'Union européenne (hors France)';
                    }
                    const names = zone.countries.map(code => (this.countryList.find(c => c.code === code) || { name: code }).name);
                    return names.length > 4 ? names.slice(0, 3).join(', ') + ' et ' + (names.length - 3) + ' autres' : names.join(', ');
                }

                const rest = zone.departments.slice();
                const parts = [];
                Object.keys(this.regionMap).forEach(region => {
                    const codes = this.regionMap[region];
                    if (codes.every(code => rest.indexOf(code) !== -1)) {
                        parts.push(region);
                        codes.forEach(code => rest.splice(rest.indexOf(code), 1));
                    }
                });
                if (rest.length > 6) {
                    parts.push(rest.length + ' départements');
                } else if (rest.length) {
                    parts.push(rest.join(', '));
                }
                return parts.join(', ');
            },
            edit(index) {
                this.editingIndex = index;
                this.search = '';
                const zone = index === null ? null : this.zones[index];
                this.draft = zone
                    ? { name: zone.name, type: zone.type, departments: zone.departments.slice(), countries: zone.countries.slice() }
                    : { name: '', type: 'departments', departments: [], countries: [] };
                this.$refs.zoneModal.show();
            },
            applyDraft() {
                const values = {
                    name: this.draft.name.trim(),
                    type: this.draft.type,
                    departments: this.draft.type === 'departments' ? this.draft.departments.slice() : [],
                    countries: this.draft.type === 'countries' ? this.draft.countries.slice() : [],
                };

                if (this.editingIndex === null) {
                    const key = this.newKey();
                    this.zones.push(Object.assign({ key, id: null }, values));
                    this.allBrackets.forEach(bracket => { this.$set(bracket.rates, key, ''); });
                } else {
                    Object.assign(this.zones[this.editingIndex], values);
                }
            },
            removeZone(index) {
                const zone = this.zones[index];

                if (!zone.id) {
                    this.zones.splice(index, 1);
                    return;
                }

                Swal.fire({
                    title: 'Supprimer la zone ' + zone.name + ' ?',
                    text: 'Ses tarifs seront supprimés avec elle.',
                    icon: 'warning',
                    showCancelButton: true,
                    cancelButtonText: 'Annuler',
                    confirmButtonColor: '#D15240',
                    confirmButtonText: 'Oui, supprimer',
                    reverseButtons: true,
                    heightAuto: false
                }).then(result => {
                    if (result.value) this.zones.splice(index, 1);
                });
            },
            newBracket(maxWeight, isLast) {
                const rates = {};
                this.zones.forEach(zone => { rates[zone.key] = ''; });

                return { uniq: this.newKey(), id: null, max_weight: maxWeight, mode: '', rates, isLast };
            },
            /** La nouvelle tranche prolonge la précédente, l'utilisateur ajuste ensuite la borne. */
            addBracket() {
                const last = this.brackets.length ? parseFloat(this.brackets[this.brackets.length - 1].max_weight) || 0 : 0;
                this.brackets.push(this.newBracket(last ? last * 2 : 10, false));
            },
            sortBrackets() {
                this.brackets.sort((a, b) => (parseFloat(a.max_weight) || 0) - (parseFloat(b.max_weight) || 0));
            },
            lowerBound(index) {
                return index === 0 ? 0 : this.brackets[index - 1].max_weight;
            },
            formatWeight(value) {
                const number = parseFloat(value) || 0;
                return number.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
            },
        },
        mounted() {
            this.countryList = JSON.parse(this.countries);
            this.departmentList = JSON.parse(this.departments);
            this.regionMap = JSON.parse(this.regions);
            this.euCodes = JSON.parse(this.europeanUnion);

            this.zones = JSON.parse(this.oldZones).map(zone => ({
                key: String(zone.key),
                id: zone.id,
                name: zone.name || '',
                type: zone.type || 'departments',
                departments: (zone.departments || []).map(String),
                countries: zone.countries || [],
            }));

            const brackets = JSON.parse(this.oldBrackets).map(bracket => {
                const rates = {};
                this.zones.forEach(zone => {
                    const value = bracket.rates ? bracket.rates[zone.key] : undefined;
                    rates[zone.key] = value !== undefined && value !== null ? value : '';
                });
                const isLast = bracket.max_weight === null || bracket.max_weight === undefined || bracket.max_weight === '';

                return { uniq: this.newKey(), id: bracket.id, max_weight: isLast ? '' : bracket.max_weight, mode: bracket.mode || '', rates, isLast };
            });

            this.brackets = brackets.filter(bracket => !bracket.isLast);
            this.sortBrackets();
            this.lastBracket = brackets.find(bracket => bracket.isLast) || this.newBracket('', true);
        }
    }
</script>

<style scoped>
    .ix-shipping-zone-choices {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 1.5rem;
        max-height: 280px;
        overflow-x: hidden;
        overflow-y: auto;
        padding: .25rem .5rem;
        border: 1px solid #dee2e6;
        border-radius: .25rem;
    }

    .ix-shipping-rate-col {
        width: 150px;
    }

    .ix-shipping-zone-choices .iti-flag {
        display: inline-block;
        vertical-align: middle;
    }
</style>
